import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ApartmentIcon from "@mui/icons-material/Apartment";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../../context/ThemeContext";
import { backgroundPattern } from "../../../config/themes";
import LocationPickerMap from "../../../components/LocationPickerMap";
import { useFormik } from "formik";

const HeroSection = ({ stats }) => {
  const [searchCity, setSearchCity] = useState("Karachi");
  const [searchType, setSearchType] = useState("");
  const navigate = useNavigate();
  const { theme } = useTheme();

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchCity) params.set("city", searchCity);
    if (searchType) params.set("allowedFor", searchType);
    if (formik.values.lat) params.set("lat", formik.values.lat);
    if (formik.values.lng) params.set("lng", formik.values.lng);
    if (formik.values.propertyAddress) params.set("propertyAddress", formik.values.propertyAddress);
    navigate(`/search-property?${params.toString()}`);
  };

  const cities = ["Karachi"];

  // for future use:
  // const cities = ["Karachi", "Lahore", "Islamabad", "Rawalpindi", "Faisalabad", "Multan"];

  const formik = useFormik({
    initialValues: {
      flatName: "",
      flatNumber: "",
      flatFloor: "",
      flatRooms: "",
      flatToilet: "",
      flatKitchen: "",
      flatRent: "",
      flatAdvance: "",
      flatMaintananceCharges: "",
      flatTrashCharges: "",
      personAllowed: "",
      scPerMonth: false,
      flatSecurityCharges: "",
      petAllowed: false,
      parkingAvailable: false,
      furnished: false,
      smokeAlarm: false,
      firstAidKit: false,
      fireExtinguisher: false,
      pool: false,
      bathTub: false,
      elevator: false,
      wifi: false,
      balcony: false,
      gym: false,
      roof: false,
      country: "Pakistan",
      city: "Karachi",
      images: [],
      previews: [],
      lateChargesPerDay: "",
      rentRelaxationDays: "",
      propertyAddress: "",
      lat: "",
      lng: "",
      mapAddress: "",
      note: "",
      is_rent: 0,
      is_active: true,
      tags: [],
      allowedFor: "family",
      landlordConditions: [],
      customConditions: "",
      rentDueDate: 5,
      noticePeriod: "1_month",
      rentIncrease: 10,
      leaseDuration: "11_months",
      // bill_uploaded_by: "landlord"
    },
  });

  return (
    <Box
      sx={{
        background: theme.gradient,
        pt: { xs: 8, md: 12 },
        pb: { xs: 12, md: 16 },
        position: "relative",
        overflow: "hidden",
        transition: "background 0.5s ease",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: backgroundPattern,
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{ textAlign: "center", mb: { xs: 5, md: 7 } }}>
          <Typography
            variant="h1"
            sx={{
              color: "white",
              fontWeight: 800,
              fontSize: { xs: "2rem", sm: "2.8rem", md: "3.8rem" },
              mb: 3,
              lineHeight: 1.2,
            }}
          >
            Pakistan's Premier
            <Box
              component="span"
              sx={{ display: "block", color: "rgba(255,255,255,0.95)" }}
            >
              Rental Management Platform
            </Box>
          </Typography>

          <Typography
            variant="h5"
            sx={{
              color: "rgba(255,255,255,0.9)",
              fontWeight: 400,
              maxWidth: 800,
              mx: "auto",
              mb: 2,
              fontSize: { xs: "1rem", md: "1.25rem" },
              lineHeight: 1.6,
            }}
          >
            Connecting property owners with verified tenants through a secure,
            transparent, and hassle-free digital platform.
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: "rgba(255,255,255,0.7)",
              maxWidth: 600,
              mx: "auto",
              mb: 4,
            }}
          >
            Free property listing | Digital agreements | Secure payments |
            Complete rental lifecycle management
          </Typography>

          {/* CTA Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              mb: 5,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={() =>
                navigate("/signup", { state: { type: "Landlord" } })
              }
              sx={{
                px: 4,
                py: 1.5,
                bgcolor: "white",
                color: theme.primary,
                fontWeight: 600,
                fontSize: "1rem",
                "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
              }}
            >
              List Your Property - It's Free
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate("/signup", { state: { type: "Tenant" } })}
              sx={{
                px: 4,
                py: 1.5,
                borderColor: "white",
                color: "white",
                fontWeight: 600,
                fontSize: "1rem",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.1)",
                  borderColor: "white",
                },
              }}
            >
              Find Your Perfect Home
            </Button>
          </Box>
        </Box>

        {/* Search Box */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 3,
            maxWidth: 900,
            mx: "auto",
            bgcolor: "white",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{ mb: 2, textAlign: "center", color: "#333" }}
          >
            Search Available Properties in KARACHI!
          </Typography>
          <Grid container spacing={2} alignItems="center">
            {/* <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Select City</InputLabel>
                <Select
                  value={searchCity}
                  label="Select City"
                  disabled
                  onChange={(e) => setSearchCity(e.target.value)}
                  startAdornment={
                    <InputAdornment position="start">
                      <LocationOnIcon sx={{ color: theme.primary }} />
                    </InputAdornment>
                  }
                >
                  {cities.map((city) => (
                    <MenuItem key={city} value={city}>
                      {city}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid> */}

            <Grid item xs={12} md={8}>
              <FormControl fullWidth>
                <LocationPickerMap formik={formik} isHero={true} />
              </FormControl>
            </Grid>
            {/* <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Property For</InputLabel>
                <Select
                  value={searchType}
                  label="Property For"
                  onChange={(e) => setSearchType(e.target.value)}
                  startAdornment={
                    <InputAdornment position="start">
                      <ApartmentIcon sx={{ color: theme.primary }} />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="family">Family</MenuItem>
                  <MenuItem value="bachelor">Bachelor</MenuItem>
                </Select>
              </FormControl>
            </Grid> */}
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                sx={{
                  // py: 1.8,
                  py: 0.8,
                  bgcolor: theme.primary,
                  fontSize: "1rem",
                  fontWeight: 600,
                  borderRadius: 2,
                  "&:hover": { bgcolor: theme.primaryHover },
                }}
              >
                Search Properties
              </Button>
            </Grid>
          </Grid>
          {/* visible for future use */}
          {/* <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: { xs: 2, md: 4 },
              mt: 3,
              flexWrap: "wrap",
            }}
          >
            <Chip
              icon={<HomeWorkIcon />}
              label={`${stats.totalProperties - 1}+ Properties`}
              sx={{
                bgcolor: theme.lightBg,
                color: theme.primary,
                fontWeight: 600,
              }}
            />
            <Chip
              icon={<PeopleIcon />}
              label={`${stats.totalTenants - 1}+ Tenants`}
              sx={{
                bgcolor: theme.lightBg,
                color: theme.primary,
                fontWeight: 600,
              }}
            />
            {stats.totalCities > 0 && (
              <Chip
                icon={<BusinessIcon />}
                label={`${stats.totalCities - 1}+ Cities`}
                sx={{
                  bgcolor: theme.lightBg,
                  color: theme.primary,
                  fontWeight: 600,
                }}
              />
            )}
          </Box> */}
        </Paper>
      </Container>
    </Box>
  );
};

export default HeroSection;
